/**
 * AgentRunPanel
 *
 * Reusable batch-run UI component for any AI agent.
 * Embeds in the agent's settings page to provide:
 *  - Scope selection (all empty / since last run / date range)
 *  - Run Now + Cancel buttons with live progress bar
 *  - Last run summary (timestamp, stats)
 *  - Collapsible recent run history
 *
 * Props:
 *  - agentType      Identifies the agent in agentRunStore / agent_run_logs
 *  - workspaceId    Workspace to run against
 *  - userId         User initiating the run (for audit log)
 *  - batchRunner    Agent-specific executor function
 */

import { useState, useEffect } from 'react';
import {
  PlayCircle,
  StopCircle,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
  CalendarRange,
} from 'lucide-react';
import { useAgentRunStore, type AgentBatchRunner } from '../../stores/prioritization/agentRunStore';
import type { RunScope, RunScopeType, AgentRunLog } from '../../lib/agentRunService';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { useCollectionsStore } from '../../stores/prioritization/collectionsStore';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  return date.toLocaleDateString();
}

function StatPill({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      {value} {label}
    </span>
  );
}

function RunLogRow({ log , allEmptyLabel = 'All unclassified items' }: { log: AgentRunLog ; allEmptyLabel?: string }) {
  const [expanded, setExpanded] = useState(false);
  const isRunning = log.status === 'running';

  const statusIcon =
    log.status === 'completed' ? <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)]" /> :
    log.status === 'failed'    ? <XCircle      className="h-3.5 w-3.5 text-[var(--coral)]" /> :
    log.status === 'cancelled' ? <XCircle      className="h-3.5 w-3.5 text-[var(--text-ter)]" /> :
                                 <Loader2      className="h-3.5 w-3.5 text-[var(--text-ter)] animate-spin" />;

  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--surface)] transition-colors"
      >
        {statusIcon}

        <span className="flex-1 text-xs text-[var(--text-sec)]">
          <span className="font-medium capitalize">{log.triggeredBy.replace('_', ' ')}</span>
          {' · '}
          <span className="text-[var(--text-ter)]">{formatRelativeTime(log.startedAt)}</span>
        </span>

        {log.status === 'completed' && (
          <div className="flex items-center gap-1">
            <StatPill label="proposed" value={log.stats.proposed} color="bg-[var(--slate-dim)] text-[var(--text-sec)]" />
            <StatPill label="skipped"  value={log.stats.skipped}  color="bg-[var(--hover-tint)] text-[var(--text-ter)]" />
            {log.stats.errors > 0 && (
              <StatPill label="errors" value={log.stats.errors} color="bg-[var(--coral-dim)] text-[var(--coral)]" />
            )}
          </div>
        )}

        {expanded
          ? <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
          : <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
        }
      </button>

      {expanded && (
        <div className="border-t border-[var(--rule)] px-3 py-2.5 bg-[var(--surface)] text-xs text-[var(--text-sec)] space-y-1">
          <div>
            <span className="font-medium text-[var(--text-ter)]">Scope:</span>{' '}
            {log.scope.type === 'all_empty'     && allEmptyLabel}
            {log.scope.type === 'since_last_run' && 'Items since last run'}
            {log.scope.type === 'date_range'     && `${log.scope.from ?? '—'} → ${log.scope.to ?? '—'}`}
            {log.scope.type === 'pool'           && 'One item pool'}
            {log.scope.type === 'cycle'          && 'One cycle'}
            {log.scope.type === 'track'          && 'One track'}
            {log.scope.type === 'collection'     && 'One collection'}
            {log.scope.type === 'items'          && `${log.scope.itemIds?.length ?? 0} chosen item${log.scope.itemIds?.length === 1 ? '' : 's'}`}
          </div>
          <div>
            <span className="font-medium text-[var(--text-ter)]">Started:</span>{' '}
            {new Date(log.startedAt).toLocaleString()}
          </div>
          {log.completedAt && (
            <div>
              <span className="font-medium text-[var(--text-ter)]">Completed:</span>{' '}
              {new Date(log.completedAt).toLocaleString()}
            </div>
          )}
          {log.status === 'completed' && (
            <div>
              <span className="font-medium text-[var(--text-ter)]">Stats:</span>{' '}
              {log.stats.processed} processed · {log.stats.proposed} proposed ·{' '}
              {log.stats.skipped} skipped · {log.stats.errors} errors
            </div>
          )}
          {log.errorMessage && (
            <div className="text-[var(--coral)]">
              <span className="font-medium">Error:</span> {log.errorMessage}
            </div>
          )}
          {isRunning && (
            <div className="text-[var(--text-ter)] flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Run in progress…
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface AgentRunPanelProps {
  agentType: string;
  workspaceId: string;
  userId?: string | null;
  batchRunner: AgentBatchRunner;
  /** What "all_empty" means for this agent (default: ATC's unclassified items). */
  allEmptyLabel?: string;
  allEmptyDescription?: string;
  /** The scopes this agent supports (its definition's scopeTypes). */
  scopeTypes?: RunScopeType[];
}

/** The things a structural scope can point at. */
type ScopeTarget = { id: string; name: string };

const STRUCTURAL: Partial<Record<RunScopeType, { label: string; desc: string; pick: string }>> = {
  pool: { label: 'One item pool', desc: 'The items in a pool someone is deciding about', pick: 'Pool' },
  cycle: { label: 'One cycle', desc: 'Every item in the pools and tracks of a cycle', pick: 'Cycle' },
  track: { label: 'One track', desc: 'The items in a track\'s pool', pick: 'Track' },
  collection: { label: 'One collection', desc: 'A collection\'s items, a smart collection\'s rule resolved now', pick: 'Collection' },
};

function useScopeTargets(workspaceId: string, type: RunScopeType): ScopeTarget[] {
  const [targets, setTargets] = useState<ScopeTarget[]>([]);
  const collections = useCollectionsStore((s) => s.collections);
  useEffect(() => {
    let cancelled = false;
    if (type === 'collection') {
      setTargets(collections.map((c) => ({ id: c.id, name: c.name })));
      return;
    }
    const table = type === 'pool' ? 'item_pools' : type === 'cycle' ? 'degap_cycles' : type === 'track' ? 'tracks' : null;
    if (!table || !workspaceId) { setTargets([]); return; }
    void (async () => {
      let q = getSupabaseClient().from(table).select('id, name').eq('workspace_id', workspaceId).order('name').limit(200);
      if (type === 'pool') q = q.is('track_id', null);
      const { data } = await q;
      if (!cancelled) setTargets(((data ?? []) as Array<{ id: string; name: string | null }>).map((r) => ({ id: r.id, name: r.name || 'Untitled' })));
    })();
    return () => { cancelled = true; };
  }, [workspaceId, type, collections]);
  return targets;
}

export function AgentRunPanel({
  agentType,
  workspaceId,
  userId,
  batchRunner,
  allEmptyLabel = 'All unclassified items',
  allEmptyDescription = 'Items with no category or altitude set',
  scopeTypes = ['all_empty', 'since_last_run', 'date_range'],
}: AgentRunPanelProps) {
  const slice = useAgentRunStore((s) => s.byAgent[agentType]);
  const startRun = useAgentRunStore((s) => s.startRun);
  const cancelRun = useAgentRunStore((s) => s.cancelRun);
  const fetchRecentLogs = useAgentRunStore((s) => s.fetchRecentLogs);

  const [scopeType, setScopeType] = useState<RunScopeType>('all_empty');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [targetId, setTargetId] = useState('');
  const targets = useScopeTargets(workspaceId, scopeType);
  const structural = STRUCTURAL[scopeType];

  useEffect(() => {
    if (workspaceId) void fetchRecentLogs(workspaceId, agentType);
  }, [workspaceId, agentType, fetchRecentLogs]);

  const isRunning = slice?.status === 'running';
  const progress = slice?.progress ?? { done: 0, total: 0 };
  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;
  const lastLog = slice?.lastRunLog ?? null;
  const recentLogs = slice?.recentLogs ?? [];

  function handleRun() {
    if (structural && !targetId) return;
    const scope: RunScope =
      scopeType === 'date_range' ? { type: 'date_range', from: dateFrom || undefined, to: dateTo || undefined }
      : scopeType === 'pool' ? { type: 'pool', poolId: targetId }
      : scopeType === 'cycle' ? { type: 'cycle', cycleId: targetId }
      : scopeType === 'track' ? { type: 'track', trackId: targetId }
      : scopeType === 'collection' ? { type: 'collection', collectionId: targetId }
      : { type: scopeType };

    void startRun({
      agentType,
      workspaceId,
      triggeredBy: 'manual',
      scope,
      runByUserId: userId ?? null,
      batchRunner,
    });
  }

  return (
    <div className="space-y-4">
      {/* Scope selector */}
      <div>
        <label className="block text-xs font-medium text-[var(--text-sec)] mb-2">Run scope</label>
        <div className="space-y-2">
          {(
            [
              { value: 'all_empty',      label: allEmptyLabel, desc: allEmptyDescription },
              { value: 'since_last_run', label: 'Since last run',         desc: 'Items created after the most recent completed run' },
              { value: 'date_range',     label: 'Custom date range',       desc: 'Items created within a specific window' },
              ...(['pool', 'cycle', 'track', 'collection'] as RunScopeType[]).map((t) => ({ value: t, label: STRUCTURAL[t]!.label, desc: STRUCTURAL[t]!.desc })),
            ] as { value: RunScopeType; label: string; desc: string }[]
          ).filter((opt) => scopeTypes.includes(opt.value)).map((opt) => (
            <label
              key={opt.value}
              className={`flex items-start gap-3 p-3  border cursor-pointer transition-colors ${
                scopeType === opt.value
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-[var(--rule)] hover:border-[var(--rule)]'
              }`}
            >
              <input
                type="radio"
                name={`scope-${agentType}`}
                value={opt.value}
                checked={scopeType === opt.value}
                onChange={() => { setScopeType(opt.value); setTargetId(''); }}
                className="mt-0.5 accent-primary-600"
              />
              <div>
                <span className="text-sm font-medium text-[var(--text-pri)]">{opt.label}</span>
                <p className="text-xs text-[var(--text-ter)] mt-0.5">{opt.desc}</p>
              </div>
            </label>
          ))}
        </div>

        {/* Structural scope target */}
        {structural && (
          <div className="mt-3 max-w-md">
            <label className="block text-xs font-medium text-[var(--text-sec)] mb-1" htmlFor={`scope-target-${agentType}`}>{structural.pick}</label>
            <select
              id={`scope-target-${agentType}`}
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              className="w-full px-2.5 py-1.5 text-sm border border-[var(--rule)] bg-[var(--surface)] focus:ring-1 focus:ring-[var(--mustard)] focus:outline-none"
            >
              <option value="">Choose…</option>
              {targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {!targets.length && <p className="text-[11px] text-[var(--text-ter)] mt-1">None in this workspace yet.</p>}
          </div>
        )}

        {/* Date range pickers */}
        {scopeType === 'date_range' && (
          <div className="mt-3 flex items-center gap-3 max-w-md">
            <CalendarRange className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
            <div className="flex items-center gap-2 flex-1">
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="flex-1 px-2.5 py-1.5 text-sm border border-[var(--rule)] focus:ring-1 focus:ring-primary-500 focus:outline-none"
              />
              <span className="text-xs text-[var(--text-ter)]">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="flex-1 px-2.5 py-1.5 text-sm border border-[var(--rule)] focus:ring-1 focus:ring-primary-500 focus:outline-none"
              />
            </div>
          </div>
        )}
      </div>

      {/* Run / Cancel button + progress */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          {!isRunning ? (
            <button
              type="button"
              onClick={handleRun}
              disabled={!!structural && !targetId}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
            >
              <PlayCircle className="h-4 w-4" />
              Run Now
            </button>
          ) : (
            <button
              type="button"
              onClick={() => cancelRun(agentType)}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--coral)] hover:bg-[var(--coral)] text-white text-sm font-medium transition-colors"
            >
              <StopCircle className="h-4 w-4" />
              Cancel
            </button>
          )}

          {isRunning && (
            <span className="text-sm text-[var(--text-sec)]">
              {progress.total > 0
                ? `${progress.done} / ${progress.total} items`
                : 'Starting…'}
            </span>
          )}

          {slice?.status === 'completed' && !isRunning && (
            <span className="flex items-center gap-1.5 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
              <CheckCircle2 className="h-4 w-4" />
              Completed — {lastLog?.stats.proposed ?? 0} proposed
            </span>
          )}

          {slice?.status === 'failed' && (
            <span className="flex items-center gap-1.5 text-sm text-[var(--coral)]">
              <AlertCircle className="h-4 w-4" />
              {slice.error ?? 'Run failed'}
            </span>
          )}

          {slice?.status === 'cancelled' && (
            <span className="flex items-center gap-1.5 text-sm text-[var(--text-ter)]">
              <XCircle className="h-4 w-4" />
              Cancelled
            </span>
          )}
        </div>

        {/* Progress bar */}
        {isRunning && progress.total > 0 && (
          <div className="w-full max-w-md">
            <div className="h-1.5 bg-[var(--hover-tint)] rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-[var(--text-ter)] mt-1">{pct}% complete</p>
          </div>
        )}
      </div>

      {/* Last run summary */}
      {lastLog && !isRunning && (
        <div className="flex items-center gap-2 text-xs text-[var(--text-ter)] bg-[var(--surface)] border border-[var(--rule)] px-3 py-2">
          <Clock className="h-3.5 w-3.5 flex-shrink-0" />
          <span>
            Last run {formatRelativeTime(lastLog.startedAt)}
            {lastLog.status === 'completed' && (
              <> · {lastLog.stats.proposed} proposed · {lastLog.stats.skipped} skipped</>
            )}
          </span>
        </div>
      )}

      {/* Run history */}
      {recentLogs.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setHistoryOpen((o) => !o)}
            className="flex items-center gap-1.5 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
          >
            {historyOpen
              ? <ChevronDown  className="h-3.5 w-3.5" />
              : <ChevronRight className="h-3.5 w-3.5" />
            }
            Run history ({recentLogs.length})
          </button>

          {historyOpen && (
            <div className="mt-2 space-y-1.5">
              {recentLogs.map((log) => (
                <RunLogRow key={log.id} log={log} allEmptyLabel={allEmptyLabel} />
              ))}
            </div>
          )}
        </div>
      )}

      {slice?.logsLoading && (
        <div className="flex items-center gap-2 text-xs text-[var(--text-ter)]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading run history…
        </div>
      )}
    </div>
  );
}
